import { useState } from 'react'
import { Image, Modal, Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Unlink, Lock, EllipsisVertical } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { images } from '../../../assets'

interface CashierHeaderProps {
  deviceName?: string
  branchName?: string
  onChangeCashierPinPress?: () => void
  onUnpairDevicePress?: () => void
}

export function CashierHeader({
  deviceName,
  branchName,
  onChangeCashierPinPress,
  onUnpairDevicePress,
}: CashierHeaderProps) {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const showDeviceInfo = Boolean(deviceName && branchName)

  const handleChangeCashierPinPress = () => {
    setMenuOpen(false)
    onChangeCashierPinPress?.()
  }

  const handleUnpairDevicePress = () => {
    setMenuOpen(false)
    onUnpairDevicePress?.()
  }

  return (
    <View className="relative flex-row items-center justify-between px-6 py-4">
      <Image
        source={images.logoLight}
        style={{ width: 80, height: 50 }}
        resizeMode="contain"
        accessibilityLabel={t('cashierHeader.logoAccessibilityLabel')}
      />

      {showDeviceInfo && (
        <View className="absolute inset-x-24 top-0 bottom-0 flex-row items-center justify-center gap-4">
          <Text className="text-lg lg:text-xl font-medium text-secondary">{branchName}</Text>
          <View className="h-1.5 w-1.5 rounded-full bg-accent" />
          <Text className="text-lg lg:text-xl font-medium text-secondary">{deviceName}</Text>
        </View>
      )}

      <View className="min-w-[40px] items-end">
        {showDeviceInfo && (
          <Pressable
            onPress={() => setMenuOpen(true)}
            accessibilityLabel={t('cashierHeader.menuButton')}
            accessibilityRole="button"
            hitSlop={8}
          >
            <EllipsisVertical size={20} color={colors.text.primary} strokeWidth={2} />
          </Pressable>
        )}
      </View>

      <Modal
        visible={menuOpen}
        transparent
        animationType="none"
        onRequestClose={() => setMenuOpen(false)}
      >
        <Pressable className="flex-1" onPress={() => setMenuOpen(false)}>
          <View className="absolute right-6 top-24 min-w-[180px] overflow-hidden rounded-md border border-border bg-canvas-card shadow-md">
            <Pressable
              className="flex-row items-center gap-3 px-4 py-3 active:bg-border"
              onPress={handleChangeCashierPinPress}
              accessibilityRole="menuitem"
              accessibilityLabel={t('cashierHeader.changeCashierPinItem')}
            >
              <Lock size={18} color={colors.text.primary} strokeWidth={1.8} />
              <Text className="text-base text-primary">
                {t('cashierHeader.changeCashierPinItem')}
              </Text>
            </Pressable>
            <Pressable
              className="flex-row items-center gap-3 px-4 py-3 active:bg-border"
              onPress={handleUnpairDevicePress}
              accessibilityRole="menuitem"
              accessibilityLabel={t('cashierHeader.unpairItem')}
            >
              <Unlink size={18} color={colors.text.primary} strokeWidth={1.8} />
              <Text className="text-base text-primary">{t('cashierHeader.unpairItem')}</Text>
            </Pressable>
          </View>
        </Pressable>
      </Modal>
    </View>
  )
}
